import { Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { List, ListListsResponse } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2/service.pb';
import { UseInfiniteQueryResult } from 'react-query';
import { Virtuoso } from 'react-virtuoso';
import { theme } from '@frontend/theme';
import { TrayMode } from '../types';
import { RenameListItem } from './rename-list-item';
import { SavedListItem } from './saved-list-item';
import { EmptyState, ErrorState, ListFooter, LoadingState } from './saved-list-modal-components';

type SavedListModalContent = {
  isDebouncing: boolean;
  contactLists: List[];
  listIdToRename?: string;
  setListIdToRename: (listId?: string) => void;
  renameContactList: (list: List) => Promise<void>;
  selectedContactLists: string[];
  handleCheck: (listId?: string) => void;
  handleDelete: (listId?: string) => void;
  contactListsQueryResult: UseInfiniteQueryResult<ListListsResponse>;
  recipientsToUpload: Recipient[];
  setMode: (mode: TrayMode) => void;
  trackingIdPrefix: string;
};

export const SavedListModalContent = ({
  contactLists,
  contactListsQueryResult,
  handleCheck,
  handleDelete,
  isDebouncing,
  renameContactList,
  selectedContactLists,
  setListIdToRename,
  listIdToRename,
  recipientsToUpload,
  setMode,
  trackingIdPrefix,
}: SavedListModalContent) => {
  const { isFetchingNextPage, isError, isFetched, hasNextPage, refetch, fetchNextPage } = contactListsQueryResult;
  const hasMore = isFetched && !!hasNextPage;

  if (isError) {
    return <ErrorState retry={() => refetch()} />;
  }

  if (isDebouncing) {
    return <LoadingState />;
  }

  if (contactLists.length > 0) {
    return (
      <Virtuoso
        style={{ height: `calc(100% - ${theme.spacing(9)})` }}
        data={contactLists}
        totalCount={contactLists.length}
        itemContent={(_index, list) =>
          listIdToRename && listIdToRename === list.listId ? (
            <RenameListItem
              key={list.listId}
              list={list}
              setListIdToRename={setListIdToRename}
              renameContactList={renameContactList}
              trackingIdPrefix={trackingIdPrefix}
            />
          ) : (
            <SavedListItem
              key={list.listId}
              list={list}
              checked={selectedContactLists.includes(list.listId ?? '')}
              onCheck={() => handleCheck(list.listId)}
              onDelete={() => handleDelete(list.listId)}
              setListIdToRename={setListIdToRename}
              aria-label={`${list.name} saved list`}
              trackingIdPrefix={trackingIdPrefix}
            />
          )
        }
        endReached={() => fetchNextPage({ cancelRefetch: false })}
        followOutput='smooth'
        components={{
          Footer: () => <ListFooter hasMore={hasMore} isFetchingNextPage={isFetchingNextPage} />,
        }}
      />
    );
  }

  if (recipientsToUpload.length === 0) {
    return <EmptyState setMode={setMode} trackingIdPrefix={trackingIdPrefix} />;
  }

  return null;
};
